import { registerSingleton, registerTransient, resolve } from '@/infrastructure/container';

import { Analytics, TOKEN_IAnalytics } from '@/services/Analytics';
import { AppBus, TOKEN_IAppBus } from '@/services/AppBus';
import { BrowserLocale, TOKEN_IBrowserLocale } from '@/services/BrowserLocale';
import { BomCompute, TOKEN_IBomCompute } from '@/services/BomCompute';
import { BundleAssembler, TOKEN_IBundleAssembler } from '@/services/BundleAssembler';
import { Clock, TOKEN_IClock } from '@/services/Clock';
import { ColumnMapper, TOKEN_IColumnMapper } from '@/services/ColumnMapper';
import { CsvParser, TOKEN_ICsvParser } from '@/services/CsvParser';
import { DateParser, TOKEN_IDateParser } from '@/services/DateParser';
import { DomKeyboard, TOKEN_IDomKeyboard } from '@/services/DomKeyboard';
import { DomTheme, TOKEN_IDomTheme } from '@/services/DomTheme';
import { FileOutput, TOKEN_IFileOutput } from '@/services/FileOutput';
import { LocalStorage, TOKEN_ILocalStorage } from '@/services/LocalStorage';
import { Logger, TOKEN_ILogger } from '@/services/Logger';

import { AppStore, TOKEN_AppStore } from '@/views/AppStore';
import { ImportsStore, TOKEN_ImportsStore } from '@/views/ImportsStore';
import { LocaleStore, TOKEN_LocaleStore } from '@/views/LocaleStore';
import { OnboardingStore, TOKEN_OnboardingStore } from '@/views/OnboardingStore';
import { ResultsStore, TOKEN_ResultsStore } from '@/views/ResultsStore';
import { SettingsStore, TOKEN_SettingsStore } from '@/views/SettingsStore';
import { SimulationStore, TOKEN_SimulationStore } from '@/views/SimulationStore';
import { ThemeStore, TOKEN_ThemeStore } from '@/views/ThemeStore';
import { ToastStore, TOKEN_ToastStore } from '@/views/ToastStore';
import { WarningsStore, TOKEN_WarningsStore } from '@/views/WarningsStore';
import { ImportSlotStore, TOKEN_ImportSlotStore } from '@/views/transient/ImportSlotStore';

import { IdGen, TOKEN_IIdGen } from '@/services/IdGen';

// Only place register* is called. Dependency graph lives here.
export function ud_di(): void {
  registerSingleton(TOKEN_ILogger, () => new Logger());
  registerSingleton(TOKEN_IAppBus, (r) => new AppBus(r(TOKEN_ILogger)));
  registerSingleton(TOKEN_IClock, () => new Clock());
  registerSingleton(TOKEN_IIdGen, () => new IdGen());
  registerSingleton(TOKEN_ILocalStorage, () => new LocalStorage());
  registerSingleton(TOKEN_IDomTheme, () => new DomTheme());
  registerSingleton(TOKEN_IDomKeyboard, (r) => new DomKeyboard(r(TOKEN_IAppBus)));
  registerSingleton(TOKEN_IBrowserLocale, () => new BrowserLocale());
  registerSingleton(TOKEN_ICsvParser, () => new CsvParser());
  registerSingleton(TOKEN_IColumnMapper, () => new ColumnMapper());
  registerSingleton(TOKEN_IDateParser, () => new DateParser());
  registerSingleton(TOKEN_IBomCompute, () => new BomCompute());
  registerSingleton(TOKEN_IBundleAssembler, (r) => new BundleAssembler(r(TOKEN_IDateParser)));
  registerSingleton(TOKEN_IFileOutput, () => new FileOutput());
  registerSingleton(TOKEN_IAnalytics, (r) => new Analytics(r(TOKEN_ILogger)));

  registerSingleton(TOKEN_AppStore, (r) => new AppStore(r(TOKEN_IDomKeyboard)));
  registerSingleton(
    TOKEN_ThemeStore,
    (r) => new ThemeStore(r(TOKEN_IAppBus), r(TOKEN_ILocalStorage), r(TOKEN_IDomTheme)),
  );
  registerSingleton(TOKEN_ToastStore, (r) => new ToastStore(r(TOKEN_IAppBus), r(TOKEN_IIdGen)));
  registerSingleton(
    TOKEN_SettingsStore,
    (r) => new SettingsStore(r(TOKEN_IClock), r(TOKEN_ILocalStorage)),
  );
  registerSingleton(TOKEN_WarningsStore, (r) => new WarningsStore(r(TOKEN_IAppBus)));
  registerSingleton(
    TOKEN_LocaleStore,
    (r) => new LocaleStore(r(TOKEN_IBrowserLocale), r(TOKEN_ILocalStorage), r(TOKEN_ILogger)),
  );
  registerSingleton(TOKEN_OnboardingStore, (r) => new OnboardingStore(r(TOKEN_ILocalStorage)));

  registerTransient(
    TOKEN_ImportSlotStore,
    (r) =>
      new ImportSlotStore(
        r(TOKEN_IAppBus),
        r(TOKEN_ICsvParser),
        r(TOKEN_IColumnMapper),
        r(TOKEN_ILogger),
        r(TOKEN_LocaleStore),
      ),
  );

  registerSingleton(
    TOKEN_ImportsStore,
    () => new ImportsStore(() => resolve(TOKEN_ImportSlotStore)),
  );

  registerSingleton(
    TOKEN_SimulationStore,
    (r) =>
      new SimulationStore(
        r(TOKEN_IAppBus),
        r(TOKEN_IClock),
        r(TOKEN_ILogger),
        r(TOKEN_IBundleAssembler),
        r(TOKEN_IBomCompute),
        r(TOKEN_ImportsStore),
        r(TOKEN_SettingsStore),
        r(TOKEN_WarningsStore),
        r(TOKEN_IAnalytics),
        r(TOKEN_LocaleStore),
      ),
  );

  registerSingleton(
    TOKEN_ResultsStore,
    (r) =>
      new ResultsStore(
        r(TOKEN_IAppBus),
        r(TOKEN_IClock),
        r(TOKEN_IFileOutput),
        r(TOKEN_SimulationStore),
        r(TOKEN_LocaleStore),
      ),
  );
}
